import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SparklesIcon, CurrencyDollarIcon, PlusIcon, XMarkIcon, ChevronDownIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/outline';

import { IUserModel, MusicGenerationMode } from '@packages/global';
import { useCoinStore } from '../../stores/coinStore';
import { ModelSelector } from '../common/form';
import AudioUpload from '../common/AudioUpload';
import LyricsGeneratorModal from './LyricsGeneratorModal';

export interface MusicFormData {
  prompt: string;
  lyrics: string;
  duration: number;
  modelId: string;
  generationMode: MusicGenerationMode;
  bpm?: number;
  language?: string;
  keyscale?: string;
  timesignature?: string;
  referenceAudioUrl?: string;
}

interface Props {
  formData: MusicFormData;
  models: IUserModel[];
  modelsLoading: boolean;
  onFormDataChange: (data: Partial<MusicFormData>) => void;
  onSubmit: (e: React.FormEvent) => void;
}

const KEY_SCALES = [
  '', 'C major', 'C minor', 'C# major', 'C# minor',
  'D major', 'D minor', 'D# major', 'D# minor',
  'E major', 'E minor', 'F major', 'F minor',
  'F# major', 'F# minor', 'G major', 'G minor',
  'G# major', 'G# minor', 'A major', 'A minor',
  'A# major', 'A# minor', 'B major', 'B minor',
];

const TIME_SIGNATURES = ['', '4/4', '3/4', '6/8', '2/4', '5/4', '7/8'];

export default function MusicGenerationForm({
  formData, models, modelsLoading,
  onFormDataChange, onSubmit,
}: Props) {
  const { t } = useTranslation(['generation', 'common']);
  const { account } = useCoinStore();
  const selectedModel = models.find(m => m.id === formData.modelId);
  const modelPrice = selectedModel?.coinPrice ?? 0;
  const hasEnoughCoins = (account?.balance ?? 0) >= modelPrice;
  const isPro = formData.generationMode === 'professional';
  const canSubmit = !modelsLoading && formData.prompt.trim().length > 0 && hasEnoughCoins && !!formData.modelId;

  // LANGUAGES array moved inside component to use t()
  const LANGUAGES = [
    { value: '', label: t('generation:music.languageOptions.auto') },
    { value: 'zh', label: t('generation:music.languageOptions.zh') },
    { value: 'en', label: t('generation:music.languageOptions.en') },
    { value: 'ja', label: t('generation:music.languageOptions.ja') },
    { value: 'ko', label: t('generation:music.languageOptions.ko') },
    { value: 'es', label: t('generation:music.languageOptions.es') },
    { value: 'fr', label: t('generation:music.languageOptions.fr') },
  ];

  // formatDuration using t() for localized formatting
  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m > 0) {
      return s > 0
        ? t('generation:music.form.formatMin', { m, s })
        : t('generation:music.form.formatMinOnly', { m });
    }
    return t('generation:music.form.formatSec', { s });
  };

  const [showRefAudioUpload, setShowRefAudioUpload] = useState(false);
  const [showMusicAttrs, setShowMusicAttrs] = useState(false);
  const [showLyricsModal, setShowLyricsModal] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioPlaying, setAudioPlaying] = useState(false);

  // 音频 URL 变化时重置播放状态
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = formData.referenceAudioUrl || '';
    }
    setAudioPlaying(false);
  }, [formData.referenceAudioUrl]);

  const toggleAudioPlay = () => {
    if (!formData.referenceAudioUrl) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(formData.referenceAudioUrl);
      audioRef.current.onended = () => setAudioPlaying(false);
    }
    if (audioPlaying) {
      audioRef.current.pause();
      setAudioPlaying(false);
    } else {
      audioRef.current.play();
      setAudioPlaying(true);
    }
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        {/* 模式切换 */}
        <div className="flex rounded-lg overflow-hidden border border-border-subtle">
          <button
            type="button"
            onClick={() => onFormDataChange({ generationMode: 'simple' })}
            className={`flex-1 py-2 text-xs font-medium transition-colors cursor-pointer ${!isPro ? 'bg-primary text-white' : 'bg-bg-elevated text-gray-400 hover:text-gray-200'
              }`}
          >
            {t('generation:music.form.simpleMode')}
          </button>
          <button
            type="button"
            onClick={() => onFormDataChange({ generationMode: 'professional' })}
            className={`flex-1 py-2 text-xs font-medium transition-colors cursor-pointer ${isPro ? 'bg-primary text-white' : 'bg-bg-elevated text-gray-400 hover:text-gray-200'
              }`}
          >
            {t('generation:music.form.professionalMode')}
          </button>
        </div>

        {/* 模型选择 */}
        <ModelSelector
          models={models}
          selectedModelId={formData.modelId}
          onChange={(modelId) => onFormDataChange({ modelId })}
          loading={modelsLoading}
          placeholder={t('generation:music.form.selectModel')}
        />


        {/* 主输入区 */}
        <div className="bg-bg-surface rounded-2xl p-4 border border-border-subtle focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-colors">
          {/* 风格描述 */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-gray-500">
                {isPro ? t('generation:music.form.captionLabel') : t('generation:music.form.describeMusic')}
              </span>
              <span className="text-xs text-gray-600">{formData.prompt.length}/2000</span>
            </div>
            <textarea
              value={formData.prompt}
              onChange={(e) => onFormDataChange({ prompt: e.target.value })}
              maxLength={2000}
              rows={isPro ? 4 : 3}
              required
              className="w-full bg-transparent border-none focus:ring-0 text-gray-50 placeholder-gray-500 text-sm resize-none py-0"
              placeholder={isPro
                ? t('generation:music.form.captionPlaceholder')
                : t('generation:music.form.simplePlaceholder')}
            />
          </div>

          {/* 专业模式：歌词 */}
          {isPro && (
            <div className="mt-3 pt-3 border-t border-border-subtle">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-gray-500">{t('generation:music.form.lyricsLabel')}</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowLyricsModal(true)}
                    className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors cursor-pointer"
                  >
                    <SparklesIcon className="w-3 h-3" /> {t('generation:music.form.aiGenerateLyrics')}
                  </button>
                  <span className="text-xs text-gray-600">{formData.lyrics.length}/5000</span>
                </div>
              </div>
              <textarea
                value={formData.lyrics}
                onChange={(e) => onFormDataChange({ lyrics: e.target.value })}
                maxLength={5000}
                rows={12}
                className="w-full bg-transparent border-none focus:ring-0 text-gray-50 placeholder-gray-500 text-xs resize-none py-0 font-mono"
                placeholder={t('generation:music.form.lyricsPlaceholder')}
              />
            </div>
          )}

          {/* 时长 */}
          <div className="mt-3 pt-3 border-t border-border-subtle">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">{t('generation:music.form.durationLabel')}</span>
              <span className="text-xs text-gray-300 font-mono">{formatDuration(formData.duration)}</span>
            </div>
            <input
              type="range" min={10} max={180} step={10}
              value={formData.duration}
              onChange={(e) => onFormDataChange({ duration: parseInt(e.target.value) })}
              className="w-full accent-primary"
            />
            <div className="flex justify-between text-xs text-gray-600 mt-1">
              <span>{t('generation:music.form.durationMin')}</span><span>{t('generation:music.form.durationMax')}</span>
            </div>
          </div>

          {/* 参考音频（两种模式都显示） */}
          <div className="mt-3 pt-3 border-t border-border-subtle relative">
            <div className="flex gap-2 flex-wrap items-center">
              {/* 已选择的参考音频标签 */}
              {formData.referenceAudioUrl && (
                <div className="flex items-center gap-2 bg-bg-elevated rounded-lg px-3 py-2">
                  <button
                    type="button"
                    onClick={toggleAudioPlay}
                    className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-primary hover:text-primary/80 transition-colors"
                    title={audioPlaying ? 'Pause' : 'Play'}
                  >
                    {audioPlaying
                      ? <PauseIcon className="w-4 h-4" />
                      : <PlayIcon className="w-4 h-4" />
                    }
                  </button>
                  <span className="text-xs text-gray-300">{t('generation:music.form.refAudioSelected')}</span>
                  <button
                    type="button"
                    onClick={() => onFormDataChange({ referenceAudioUrl: undefined })}
                    className="p-0.5 text-gray-400 hover:text-red-400 transition-colors cursor-pointer"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              {/* 未选择时显示添加按钮 */}
              {!formData.referenceAudioUrl && (
                <button
                  type="button"
                  onClick={() => setShowRefAudioUpload(true)}
                  className="w-12 h-12 bg-bg-elevated hover:bg-border-default rounded-lg flex items-center justify-center transition-colors border-2 border-dashed border-border-default cursor-pointer"
                  title={t('generation:music.form.addRefAudio')}
                >
                  <PlusIcon className="h-5 w-5 text-gray-400" />
                </button>
              )}

              <span className="text-xs text-gray-500">
                {formData.referenceAudioUrl ? '' : t('generation:music.form.refAudioLabel')}
              </span>
            </div>

            {/* 弹出上传面板 */}
            {showRefAudioUpload && !formData.referenceAudioUrl && (
              <div className="absolute bottom-full left-0 mb-2 bg-bg-surface border border-border-subtle rounded-lg shadow-lg z-50 p-3 min-w-[280px]">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-gray-400">{t('generation:music.form.selectRefAudio')}</span>
                  <button
                    type="button"
                    onClick={() => setShowRefAudioUpload(false)}
                    className="text-gray-500 hover:text-gray-300 cursor-pointer"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
                <AudioUpload
                  value=""
                  onUploadSuccess={(url) => {
                    if (url) {
                      onFormDataChange({ referenceAudioUrl: url });
                      setShowRefAudioUpload(false);
                    }
                  }}
                  showAudioSelector={false}
                  showMusicSelector={true}
                  placeholder={t('generation:music.form.refAudioUploadHint')}
                />
              </div>
            )}
          </div>
        </div>

        {/* 专业模式：音乐属性 */}
        {isPro && (
          <div className="bg-bg-surface rounded-xl border border-border-subtle overflow-hidden">
            <button
              type="button"
              onClick={() => setShowMusicAttrs(v => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider hover:bg-bg-elevated transition-colors cursor-pointer"
            >
              <span>{t('generation:music.form.musicAttrs')}</span>
              <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${showMusicAttrs ? 'rotate-180' : ''}`} />
            </button>
            {showMusicAttrs && (
              <div className="px-4 pb-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">{t('generation:music.form.bpmLabel')}</label>
                    <input
                      type="number" min={40} max={240}
                      value={formData.bpm ?? ''}
                      onChange={(e) => onFormDataChange({ bpm: e.target.value ? parseInt(e.target.value) : undefined })}
                      placeholder={t('generation:music.form.autoLabel')}
                      className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-50 placeholder-gray-600 focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">{t('generation:music.form.timeSignatureLabel')}</label>
                    <select
                      value={formData.timesignature ?? ''}
                      onChange={(e) => onFormDataChange({ timesignature: e.target.value || undefined })}
                      className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-50 focus:outline-none focus:border-primary cursor-pointer"
                    >
                      {TIME_SIGNATURES.map(ts => (
                        <option key={ts} value={ts}>{ts || t('generation:music.form.autoLabel')}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">{t('generation:music.form.keyScaleLabel')}</label>
                    <select
                      value={formData.keyscale ?? ''}
                      onChange={(e) => onFormDataChange({ keyscale: e.target.value || undefined })}
                      className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-50 focus:outline-none focus:border-primary cursor-pointer"
                    >
                      {KEY_SCALES.map(k => (
                        <option key={k} value={k}>{k || t('generation:music.form.autoLabel')}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">{t('generation:music.form.languageLabel')}</label>
                    <select
                      value={formData.language ?? ''}
                      onChange={(e) => onFormDataChange({ language: e.target.value || undefined })}
                      className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-50 focus:outline-none focus:border-primary cursor-pointer"
                    >
                      {LANGUAGES.map(l => (
                        <option key={l.value} value={l.value}>{l.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 底部提交区 */}
      <div className="p-6 pt-0 space-y-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full bg-gradient-to-br from-primary to-primary-light hover:shadow-[0_0_20px_rgba(255,92,0,0.3)] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          {modelsLoading ? t('generation:music.form.submitLoading') : t('generation:music.form.submitButton')}
        </button>
        {!modelsLoading && modelPrice > 0 && (
          <div className="flex items-center justify-between text-xs px-1">
            <div className="flex items-center gap-1 text-gray-500">
              <CurrencyDollarIcon className="w-3.5 h-3.5" />
              <span>{t('generation:music.form.cost')} <span className="font-mono text-primary">{modelPrice}</span></span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">{t('generation:music.form.balance')}</span>
              <span className={`font-mono ${hasEnoughCoins ? 'text-gray-300' : 'text-red-400'}`}>
                {account?.balance ?? 0}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* 歌词生成弹窗 */}
      <LyricsGeneratorModal
        open={showLyricsModal}
        initialStyle={formData.prompt}
        onConfirm={({ caption, lyrics, suggestedBpm, suggestedKey }) => {
          const update: Partial<typeof formData> = { lyrics };
          if (caption) update.prompt = caption;
          if (suggestedBpm) update.bpm = suggestedBpm;
          if (suggestedKey) update.keyscale = suggestedKey;
          onFormDataChange(update);
        }}
        onClose={() => setShowLyricsModal(false)}
      />
    </form>
  );
}
